import type { PropTypes } from './ProgressBar.types';
import type { CSSProperties, ReactElement } from 'react';

import { Box, Flex, Text } from '@rango-dev/whale';
import { Circle, CircleCheckBig } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { numberToString } from '../../../utils/Numbers';
import { displayUsdValue } from '../../Profile/ProfileSummary/ProfileSummary.helpers';
import { CampaignType } from '../Leaderboard/Leaderboard.types';

import styles from './ProgressBar.module.css';

const MAX_PROGRESS = 100;
const STATUS_ICON_SIZE = 16;

export function ProgressBar(props: PropTypes): ReactElement {
  const { end, start, prize, doneCriteriaAmount, icon, type = CampaignType.TRANSACTION } = props;
  const [progress, setProgress] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    if (doneCriteriaAmount >= start && doneCriteriaAmount <= end) {
      setProgress(((doneCriteriaAmount - start) / (end - start)) * MAX_PROGRESS);
    } else if (doneCriteriaAmount > end) {
      setProgress(MAX_PROGRESS);
    } else {
      setProgress(0);
    }
  }, [doneCriteriaAmount, end, start]);

  const progressStarted = progress > 0;
  const progressFinished = progress === MAX_PROGRESS && doneCriteriaAmount >= end;

  const rangeLabel = `${type === CampaignType.VOLUME ? '$' : ''}${numberToString(start.toString())} - ${type === CampaignType.VOLUME ? '$' : ''}${numberToString(end.toString())}`;

  return (
    <Flex
      position="relative"
      align="center"
      justify="end"
      gap="2"
      width={{ initial: '100%', md: 'auto' }}
      className={styles.container}>
      <Flex
        align="center"
        justify="start"
        gap="1"
        flexShrink="0"
        height="24px"
        width={{ initial: '110px', md: '140px' }}>
        {progressStarted ? (
          progressFinished ? (
            <CircleCheckBig size={STATUS_ICON_SIZE} className={styles.statusIcon} aria-hidden />
          ) : (
            <Circle size={STATUS_ICON_SIZE} className={styles.statusIcon} aria-hidden />
          )
        ) : (
          <Box width={{ initial: `${STATUS_ICON_SIZE}px` }} />
        )}
        <Text as="span" size="2" weight="medium" className={styles.prizeTitle}>
          {t('Prize')}: {displayUsdValue(prize)}
        </Text>
      </Flex>

      <div className={styles.progressArea}>
        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{ ['--progress']: `${progress}%` } as CSSProperties}>
            <Text
              as="span"
              size="1"
              weight="medium"
              className={styles.rangeLabelActive}
              aria-hidden>
              {rangeLabel}
            </Text>
          </div>
          <Text as="span" size="1" weight="medium" className={styles.rangeLabelInactive}>
            {rangeLabel}
          </Text>
        </div>
        <div className={styles.trailingIcon}>
          <img src={icon} alt={t('Prize')} width={20} height={20} />
        </div>
      </div>
    </Flex>
  );
}
